/**
 * Human labels for every templated notification event.
 *
 * A TOTAL Record over `NOTIFICATION_EVENT_CODES`: adding an event code without
 * a label here fails typecheck, which is the point — a new event must never
 * reach the admin as a bare snake_case key. Kept in its own module so the two
 * settings pages can import it without dragging a component graph along.
 */
import type { NOTIFICATION_EVENT_CODES } from '@mlm/shared';

export const EVENT_LABELS: Record<(typeof NOTIFICATION_EVENT_CODES)[number], string> = {
  order_placed: 'Order placed',
  order_pending_payment: 'Order pending payment',
  order_awaiting_shipping_fee: 'Order awaiting shipping fee (manual courier)',
  order_shipping_fee_set: 'Shipping fee set — order ready to pay',
  payment_submitted: 'Payment submitted for review',
  dropship_sale: 'Sale via your replicated link',
  cashout_requested: 'Cash-out requested',
  cashout_rejected: 'Cash-out rejected',
  cashout_paid: 'Cash-out processed',
  topup_submitted: 'Wallet top-up submitted',
  topup_approved: 'Wallet top-up approved',
  topup_rejected: 'Wallet top-up rejected',
  autoship_completed: 'Auto-ship order placed',
  autoship_skipped: 'Auto-ship skipped (low balance)',
  rank_up: 'Rank up (Stairstep)',
  voucher_issued: 'Gift check earned or issued',
  voucher_redeemed: 'Gift check used at the till',
  voucher_expiring: 'Gift check expiring in 3 days',
  birthday_voucher_available: 'Birthday gift check ready to claim',
  pos_sale_completed: 'Till sale receipt (to the customer)',
  // LMS Phase 2 — learners
  course_enrolled: 'Course enrolled (access granted)',
  question_answered: 'Question answered',
  test_passed: 'Test passed (graded)',
  test_failed: 'Test not passed (graded)',
  certificate_issued: 'Certificate issued',
  learning_reminder: 'Learning reminder (inactive learner)',
  course_announcement: 'Course announcement (creator → enrolled students)',
  subscription_renewed: 'Subscription renewed automatically',
  subscription_renewal_failed: 'Subscription renewal did not go through',
  subscription_expiring: 'Subscription ends in 3 days (no auto-renew)',
  subscription_card_renewed: 'Subscription renewed by card (Stripe)',
  // LMS Phase 2 — creators
  creator_approved: 'Creator approved',
  creator_rejected: 'Creator application rejected',
  creator_suspended: 'Creator suspended',
  course_approved: 'Course listing approved',
  course_rejected: 'Course listing rejected',
  course_content_approved: 'Course content changes approved',
  course_content_rejected: 'Course content changes rejected',
  subscription_plan_approved: 'Creator subscription plan approved',
  subscription_plan_rejected: 'Creator subscription plan rejected',
  creator_sale: 'Creator sale (course sold)',
  creator_payout_paid: 'Creator payout paid',
  creator_payout_rejected: 'Creator payout rejected',
  creator_question: 'New learner question (creator)',
  creator_grading: 'Test awaiting grading (creator)',
};
